import React, { Fragment, createContext, useContext, useEffect, useState } from 'react';
import Section, { size } from '../../components/generic/Section';

import Toggle from '../../components/generic/Toggle';
import { animated } from '@react-spring/web';
import { mergeClassNames } from '../../utils/mergeClassnames';
import styles from './index.module.scss';

// Wrap in context provider.https://stackoverflow.com/questions/54979668/how-can-i-share-state-data-between-components-using-custom-hooks
const DebugContext = createContext(false);
const DebugGridContext = createContext(false);
export function DebugProvider({ children }) {
  let { debug, debugGrid } = useToggleDebug(false);

  return (
    <DebugContext.Provider value={debug}>
      <DebugGridContext.Provider value={debugGrid}>{children}</DebugGridContext.Provider>
    </DebugContext.Provider>
  );
}

export function useDebug() {
  return useContext(DebugContext);
}

export function useDebugGrid() {
  return useContext(DebugGridContext);
}

function useToggleDebug(initState = false) {
  let [debug, setDebug] = useState(initState);
  let [debugGrid, setDebugGrid] = useState(initState);

  useEffect(() => {
    function onKeyUp(event) {
      switch (true) {
        case event.code === 'KeyD' && event.ctrlKey:
          return void setDebug(prevState => !prevState);
        case event.code === 'KeyG' && event.ctrlKey:
          return void setDebugGrid(prevState => !prevState);
        default:
      }
    }

    window.document.body.addEventListener('keyup', onKeyUp);
    return () => window.document.body.removeEventListener('keyup', onKeyUp);
  }, []);

  return { debug, debugGrid };
}

export function DebugDeviceScrollSpring({ spring, title }) {
  return (
    <>
      <h4>{title}</h4>
      {Object.keys(spring).map(key => {
        return (
          <Fragment key={key}>
            {key}: <animated.span>{spring[key]}</animated.span>
          </Fragment>
        );
      })}
    </>
  );
}

export function DebugDeviceScrollState({ state, title }) {
  return (
    <>
      <h4>{title}</h4>
      {Object.keys(state).map(key => {
        switch (typeof state[key]) {
          case 'number':
          case 'string':
            return (
              <Fragment key={key}>
                {key}: {state[key]}
              </Fragment>
            );
          default:
            return (
              <Fragment key={key}>
                {key}: {typeof state[key]}
              </Fragment>
            );
        }
      })}
    </>
  );
}

export function DebugDeviceScroll({ children }) {
  return (
    <div className={styles.debugger}>
      <h2>scrollPositionState</h2>
      {children}
    </div>
  );
}

export function DebugModal({ children, debug = false, full = false, className = '', style = {} }) {
  return (
    <div
      className={mergeClassNames(styles.debugOverlay, debug ? styles.debug : null, full ? styles.full : null)}
      style={style}
    >
      <div className={mergeClassNames(styles.inner, className)}>{children}</div>
    </div>
  );
}

// let deviceColumnCounts = { mobile: 6, tablet: 8, desktop: 12 };
let gridTypes = { none: 'noneMargins', default: 'defaultMargins', grid: 'gridMargins' };

export function DebugGrid({ initGridType = gridTypes.default }) {
  let [gridType, setGridType] = useState(() => initGridType);
  let columnCount = 12;

  return (
    <>
      <Section
        className={mergeClassNames(styles.debugGrid, styles[gridType])}
        innerClassName={styles.gridInner}
        size={size.max}
      >
        {Array.apply(null, Array(columnCount)).map((_, index) => {
          return (
            <Fragment key={index}>
              <div className={styles.column} />
              {index !== columnCount - 1 && <div className={styles.gutter} />}
            </Fragment>
          );
        })}
      </Section>
      <div className={styles.toggleOverlay}>
        <Toggle
          handleToggle={() =>
            setGridType(gridType => (gridType === gridTypes.default ? gridTypes.none : gridTypes.default))
          }
          isOn={gridType === gridTypes.default}
        />
        <span> {gridTypes.default}</span>
        <Toggle
          handleToggle={() => setGridType(gridType => (gridType === gridTypes.grid ? gridTypes.none : gridTypes.grid))}
          isOn={gridType === gridTypes.grid}
        />
        <span> {gridTypes.grid}</span>
      </div>
    </>
  );
}

export function useLogPropChanged(label = 'unnamed', props) {
  useEffect(() => console.log(`${label} changed:`, props), [label, props]);
}

/**
 * @param {string} label
 * @returns {debugElem}
 *
 * @example
 * ```javascript
 * [0,1,2].map(debugArrayMap('numberArray'))
 * ```
 */
export function debugArrayMap(label = 'unamed') {
  /**
   * @template Type
   * @param {Type} elem array elem
   * @param {number} index array index
   * @param {Type[]}  arr source array
   * @returns {Type}
   */
  function debugElem(elem, index, arr) {
    console.groupCollapsed(`${label}: ${index}:`, elem);
    console.log(`sourceArray:`, arr);
    console.groupEnd();
    return elem;
  }
  return debugElem;
}
